import React, { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useExperience } from '../contexts/ExperienceContext';
import { MaterialField } from './atmosphere/MaterialField';
import { ExperienceChrome } from './chrome/ExperienceChrome';
import { BridgeStage } from './stages/BridgeStage';
import { CloserStage } from './stages/CloserStage';
import { CompetitorStage } from './stages/CompetitorStage';
import { DeeperStage } from './stages/DeeperStage';
import { EntryStage } from './stages/EntryStage';
import { FamilyStage } from './stages/FamilyStage';
import { MirrorStage } from './stages/MirrorStage';
import { NeedsStage } from './stages/NeedsStage';
import { PriorityStage } from './stages/PriorityStage';
import { RecommendStage } from './stages/RecommendStage';
import { RevealStage } from './stages/RevealStage';
import { UsageStage } from './stages/UsageStage';
import { WeekStage } from './stages/WeekStage';
interface ExperienceProps {
  needsLayout: 'ribbon' | 'orbit';
  showSessionMemory: boolean;
}

/** One continuous conversation: every stage replaces the last in place. */
export function Experience({
  needsLayout,
  showSessionMemory
}: ExperienceProps) {
  const {
    stage,
    accent
  } = useExperience();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [stage]);
  const current = () => {
    switch (stage) {
      case 'better-priority':
        return <PriorityStage key="better-priority" variant="better" data-id="element-52" data-mp="orzjibqrlivf" />;
      case 'better-mirror':
        return <MirrorStage key="better-mirror" data-id="element-53" data-mp="awx5t2foep82" />;
      case 'new-priority':
        return <PriorityStage key="new-priority" variant="new" data-id="element-54" data-mp="fqt09uwqdtyh" />;
      case 'new-bridge':
        return <BridgeStage key="new-bridge" data-id="element-55" data-mp="1257fwe9ryc8" />;
      case 'existing-usage':
        return <UsageStage key="existing-usage" data-id="element-56" data-mp="5yvkeaus2dtf" />;
      case 'existing-competitor':
        return <CompetitorStage key="existing-competitor" data-id="element-57" data-mp="3o4rvuvfsjdbg" />;
      case 'existing-closer':
        return <CloserStage key="existing-closer" data-id="element-58" data-mp="3s6n5cn7vkx0b" />;
      case 'reveal':
        return <RevealStage key="reveal" data-id="element-59" data-mp="3s8zoh3pdddey" />;
      case 'needs':
        return <NeedsStage key="needs" layout={needsLayout} data-id="element-60" data-mp="1yoy007d8xijx" />;
      case 'week':
        return <WeekStage key="week" data-id="element-61" data-mp="1tx63zhbbmkme" />;
      case 'recommend':
        return <RecommendStage key="recommend" data-id="element-62" data-mp="1tvmrm1ssctpw" />;
      case 'family':
        return <FamilyStage key="family" data-id="element-63" data-mp="1oymifi0n3auk" />;
      case 'deeper':
        return <DeeperStage key="deeper" data-id="element-64" data-mp="1k01i7ile0r64" />;
      case 'entry':
      default:
        return <EntryStage key="entry" data-id="element-65" data-mp="2hms2e2jvcvtc" />;
    }
  };
  return <div className="relative w-full overflow-x-hidden" data-id="element-66" data-mp="2hmieckidwyqa">
      <MaterialField accent={accent} data-id="element-67" data-mp="2cnfk7v3y0cx3" />
      <ExperienceChrome showMemory={showSessionMemory} data-id="element-68" data-mp="2cogsxsxhknbn" />
      <main className="relative w-full" data-id="element-69" data-mp="27ob92yxgk544">
        <AnimatePresence mode="wait" data-id="element-70" data-mp="27n9mpyazrw73">{current()}</AnimatePresence>
      </main>
    </div>;
}